<template>
  <generic-modal :title="$t('loan quotas')" ref="generic_modal" :is_wide="true">
    <div v-if="loan">
      <!-- Información del préstamo -->
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("loan number") }}</p>
                <p class="title is-5">{{ loan.loan_number }}</p>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("loan amount") }}</p>
                <p class="title is-4 has-text-primary">
                  {{ format_currency(loan.amount) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="level" v-if="loan.customer">
          <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("customer") }}</p>
                <p class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-user"></i>
                  </span>
                  {{ loan.customer.first_name }} {{ loan.customer.last_name }}
                </p>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("loan type") }}</p>
                <p class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-tag"></i>
                  </span>
                  {{ loan.loan_type?.name }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalles financieros -->
      <div class="box">
        <h4 class="title is-6">
          <span class="icon">
            <i class="fa fa-calculator"></i>
          </span>
          {{ $t("financial details") }}
        </h4>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("quotas") }}</label>
              <div class="control">
                <span class="tag is-info">{{ loan.quotas }}</span>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("interest rate") }}</label>
              <div class="control">
                <span class="tag is-warning">{{ loan.interest_rate }}%</span>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{
                $t("payment frequency")
              }}</label>
              <div class="control">
                <span class="tag">{{ $t(loan.payment_frequency) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{
                $t("outstanding balance")
              }}</label>
              <div class="control">
                <p class="has-text-weight-bold has-text-danger">
                  {{ format_currency(loan.outstanding_balance) }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("total paid") }}</label>
              <div class="control">
                <p class="has-text-weight-bold has-text-success">
                  {{ format_currency(loan.total_paid) }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{
                $t("grace period days")
              }}</label>
              <div class="control">
                <span class="tag is-info"
                  >{{ loan.grace_period_days }} {{ $t("days") }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{
                $t("first payment date")
              }}</label>
              <div class="control">
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-calendar-o"></i>
                  </span>
                  <span>{{ format_date(loan.first_payment_date) }}</span>
                </span>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{
                $t("next payment date")
              }}</label>
              <div class="control">
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-calendar-check-o"></i>
                  </span>
                  <span>{{ format_date(loan.next_payment_date) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla de cuotas -->
      <div class="box">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-calendar"></i>
          </span>
          {{ $t("payment schedule") }}
        </h4>

        <div class="table-container">
          <table class="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                <th>{{ $t("installment") }}</th>
                <th>{{ $t("due date") }}</th>
                <th>{{ $t("principal") }}</th>
                <th>{{ $t("interest") }}</th>
                <th>{{ $t("total amount") }}</th>
                <th>{{ $t("balance") }}</th>
                <th>{{ $t("status") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="schedule in loan.loan_payment_schedules"
                :key="schedule.installment_number"
              >
                <td>
                  <span class="tag is-primary is-rounded">
                    {{ schedule.installment_number }}
                  </span>
                </td>
                <td>
                  <span class="icon">
                    <i class="fa fa-calendar-o"></i>
                  </span>
                  {{ format_date(schedule.due_date) }}
                </td>
                <td>{{ format_currency(schedule.principal_amount) }}</td>
                <td>{{ format_currency(schedule.interest_amount) }}</td>
                <td class="has-text-weight-bold">
                  {{ format_currency(schedule.total_amount) }}
                </td>
                <td>{{ format_currency(schedule.balance_after_payment) }}</td>
                <td>
                  <span
                    class="tag"
                    :class="{
                      'is-warning': schedule.status === 'pending',
                      'is-success': schedule.status === 'paid',
                      'is-danger': schedule.status === 'overdue',
                    }"
                  >
                    <span class="icon">
                      <i
                        class="fa"
                        :class="{
                          'fa-clock-o': schedule.status === 'pending',
                          'fa-check': schedule.status === 'paid',
                          'fa-exclamation': schedule.status === 'overdue',
                        }"
                      ></i>
                    </span>
                    <span>{{ $t(schedule.status) }}</span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="notification is-danger" v-if="error" v-html="error"></div>

    <template v-slot:footer>
      <!-- <button
        class="button is-primary is-rounded"
        @click="save"
        :disabled="is_form_invalid"
      >
        <span class="icon">
          <i class="fa fa-save"></i>
        </span>
        <span>{{ $t("save") }}</span>
      </button> -->
      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-close"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import GenericModal from "@/components/generic_modal/Index.vue";
import {
  generate_error_message_text,
  del,
  post,
  put,
  get,
  format_currency,
  format_date,
} from "@/utils";

export default {
  name: "LoanQuotasModal",
  components: { GenericModal },
  data() {
    let v = this;
    return {
      loan: null,
      error: "",
    };
  },
  computed: {},
  methods: {
    format_currency,
    format_date,
    show_modal(loan_uuid = null) {
      let v = this;
      v.set_initial_state();
      v.$refs.generic_modal.show_modal();
      if (loan_uuid) {
        get("/loan/loan/quotas", { uuid: loan_uuid })
          .then((response) => {
            v.loan = response || {};
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          });
      }
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    set_initial_state() {
      let v = this;
      v.loan = null;
      v.error = "";
    },
  },
  watch: {},
};
</script>
